/**
 * Display formatting. Money, counts, weights, dates.
 *
 * MONEY IS INTEGER PAISE, END TO END. The API sends paise as integers, this file
 * turns them into strings, and no code path in between divides by 100 into a
 * float. `12345 / 100` happens to be exact, but `0.1 + 0.2` is not, and a cart
 * total assembled from float rupees eventually renders "₹1,234.5600000000001" or,
 * worse, disagrees with the server by a paise. So: integer division for the rupee
 * part, integer remainder for the paise part, string concatenation for the rest.
 *
 * Dates render in Asia/Kolkata regardless of device timezone — an order placed at
 * 11pm IST must not read as "yesterday" because the phone is set to UTC. The
 * server tells us the zone in `meta.timezone`; DEFAULT_TIMEZONE is the fallback
 * and matches the backend's default.
 */

export const DEFAULT_TIMEZONE = 'Asia/Kolkata';
export const DEFAULT_LOCALE = 'en-IN';
export const RUPEE = '₹';

// --- Money -----------------------------------------------------------------

/** Groups an integer string the Indian way: last 3 digits, then pairs. */
function groupIndian(digits: string): string {
  if (digits.length <= 3) return digits;
  const lastThree = digits.slice(-3);
  const rest = digits.slice(0, -3);
  const grouped = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${grouped},${lastThree}`;
}

export interface FormatPaiseOptions {
  /** Drop the ₹ symbol (for inputs and table cells with a currency column). */
  symbol?: boolean;
  /** Render whole rupees without ".00". Off by default: money lines up better. */
  trimZeroPaise?: boolean;
}

/**
 * Formats integer paise as rupees.
 *
 *   formatPaise(0)      -> "₹0.00"
 *   formatPaise(5)      -> "₹0.05"
 *   formatPaise(100)    -> "₹1.00"
 *   formatPaise(12345)  -> "₹123.45"
 *   formatPaise(123456) -> "₹1,234.56"
 *   formatPaise(-12345) -> "-₹123.45"
 *
 * A non-integer input is a bug upstream (someone did float maths on money), so it
 * is rounded and the value is still rendered — a visibly wrong price is better
 * than a crashed checkout — but callers must never rely on that.
 */
export function formatPaise(paise: number, options: FormatPaiseOptions = {}): string {
  const { symbol = true, trimZeroPaise = false } = options;

  if (!Number.isFinite(paise)) return symbol ? `${RUPEE}0.00` : '0.00';
  const total = Math.round(paise);

  const negative = total < 0;
  const absolute = Math.abs(total);
  // Integer division and remainder: no float ever holds an amount.
  const rupees = Math.trunc(absolute / 100);
  const remainder = absolute % 100;

  const rupeePart = groupIndian(String(rupees));
  const paisePart = String(remainder).padStart(2, '0');

  const amount = trimZeroPaise && remainder === 0 ? rupeePart : `${rupeePart}${'.'}${paisePart}`;

  return `${negative ? '-' : ''}${symbol ? RUPEE : ''}${amount}`;
}

/** `formatPaise` without the symbol — for aria-labels and input default values. */
export function formatPaiseAmount(paise: number): string {
  return formatPaise(paise, { symbol: false });
}

/**
 * Parses user-typed rupees into integer paise, or null if unparseable.
 *
 * String-based on purpose: `Number("12.34") * 100` is 1233.9999999999998.
 */
export function parseRupeesToPaise(input: string): number | null {
  const cleaned = input.trim().replace(/[\s,₹]/g, '');
  if (cleaned === '' || !/^-?\d*(\.\d{0,2})?$/.test(cleaned)) return null;

  const negative = cleaned.startsWith('-');
  const unsigned = negative ? cleaned.slice(1) : cleaned;
  const [rupeeText = '', paiseText = ''] = unsigned.split('.');
  if (rupeeText === '' && paiseText === '') return null;

  const rupees = rupeeText === '' ? 0 : Number.parseInt(rupeeText, 10);
  const paise = Number.parseInt(paiseText.padEnd(2, '0') || '0', 10);
  if (!Number.isFinite(rupees) || !Number.isFinite(paise)) return null;

  const total = rupees * 100 + paise;
  return negative ? -total : total;
}

/**
 * Applies a basis-point rate to an amount, in integers.
 *
 *   applyBps(10000, 1800) -> 1800   (18% GST on ₹100.00 is ₹18.00)
 *
 * Rounds half-up, which is what the backend does. This is for DISPLAY PREVIEWS
 * only — the authoritative total always comes from the server's quote.
 */
export function applyBps(paise: number, bps: number): number {
  return Math.round((Math.round(paise) * Math.round(bps)) / 10_000);
}

/** `1800` -> `"18%"`, `1850` -> `"18.5%"`. Integer basis points in, string out. */
export function formatBps(bps: number): string {
  const whole = Math.trunc(bps / 100);
  const fraction = Math.abs(bps % 100);
  if (fraction === 0) return `${whole}%`;
  const trimmed = String(fraction).padStart(2, '0').replace(/0$/, '');
  return `${whole}.${trimmed}%`;
}

// --- Counts ----------------------------------------------------------------

/** Pluralises without a dependency. `plural(1,'page')` -> "1 page". */
export function plural(count: number, singular: string, pluralForm?: string): string {
  const word = count === 1 ? singular : (pluralForm ?? `${singular}s`);
  return `${formatCount(count)} ${word}`;
}

/** Thousands-grouped integer count, Indian grouping. */
export function formatCount(count: number): string {
  if (!Number.isFinite(count)) return '0';
  const rounded = Math.round(count);
  const grouped = groupIndian(String(Math.abs(rounded)));
  return rounded < 0 ? `-${grouped}` : grouped;
}

export function formatPages(pages: number): string {
  return plural(pages, 'page');
}

export function formatSheets(sheets: number): string {
  return plural(sheets, 'sheet');
}

/**
 * Sheets needed for a page count at a given sided-ness.
 * Kept here because both the options screen and the price summary need it and
 * must never disagree.
 */
export function sheetsForPages(pages: number, sides: 1 | 2): number {
  if (pages <= 0) return 0;
  return sides === 2 ? Math.ceil(pages / 2) : pages;
}

// --- Weight and size -------------------------------------------------------

/**
 * Grams to a display string. Under 1kg stays in grams (couriers quote grams);
 * at or above 1kg switches to kg with two decimals, derived by integer split.
 */
export function formatWeight(grams: number): string {
  if (!Number.isFinite(grams)) return '0 g';
  const rounded = Math.round(grams);
  const absolute = Math.abs(rounded);
  const sign = rounded < 0 ? '-' : '';
  if (absolute < 1000) return `${sign}${formatCount(absolute)} g`;
  const kilos = Math.trunc(absolute / 1000);
  const remainder = Math.round((absolute % 1000) / 10); // hundredths of a kg
  const fraction = String(remainder).padStart(2, '0');
  return remainder === 0
    ? `${sign}${formatCount(kilos)} kg`
    : `${sign}${formatCount(kilos)}.${fraction} kg`;
}

/**
 * Bytes to a human size. Uses MB/GB in the binary sense the API's MAX_UPLOAD_MB
 * uses (1 MB = 1024 * 1024 bytes), so "100 MB limit" and the size we show a user
 * for their 104,857,600-byte file agree exactly.
 */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const units = ['KB', 'MB', 'GB'] as const;
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const unit = units[unitIndex] ?? 'KB';
  // File sizes are approximate by nature, so a float here is harmless — unlike money.
  const digits = value < 10 && unitIndex > 0 ? 1 : 0;
  return `${value.toFixed(digits)} ${unit}`;
}

/** `100` (MB) -> "100 MB", straight from meta.upload.max_upload_mb. */
export function formatMegabytes(megabytes: number): string {
  return `${formatCount(megabytes)} MB`;
}

// --- Dates and times -------------------------------------------------------

function toDate(value: Date | string | number): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * "Sat 27 Sep" / "Sat 27 – Mon 29 Sep" / "Mon 29 Sep – Thu 2 Oct".
 *
 * For a delivery window, which is a pair of CALENDAR DATES rather than
 * instants — the server already worked them out in the shop's timezone. So
 * they are formatted in UTC deliberately: reading "2026-09-27" as midnight UTC
 * and then rendering it in a zone behind UTC would show the 26th, moving a
 * delivery promise by a day for anyone travelling.
 *
 * The weekday is what makes it concrete. "Delivered in 2-4 days" makes a
 * customer count on their fingers; "Sat 27 – Mon 29 Sep" does not.
 */
export function formatDayRange(from: string, to: string): string {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return '';

  // Composed from single-field formatters rather than one call with three
  // fields: en-IN punctuates that as "Sun, 27 Sept", and a range of those
  // ("Sun, 27 – Tue, 29 Sept") reads like a list of four things.
  const field = (date: Date, options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(DEFAULT_LOCALE, { ...options, timeZone: 'UTC' }).format(date);

  const part = (date: Date, withMonth: boolean) =>
    [
      field(date, { weekday: 'short' }),
      field(date, { day: 'numeric' }),
      withMonth ? field(date, { month: 'short' }) : '',
    ]
      .filter(Boolean)
      .join(' ');

  if (from === to) return part(start, true);
  // The month is stated once when both days share it, twice when they do not.
  const sameMonth = from.slice(0, 7) === to.slice(0, 7);
  return `${part(start, !sameMonth)} – ${part(end, true)}`;
}

export interface DateFormatOptions {
  /** IANA zone; defaults to Asia/Kolkata. Pass `meta.timezone` when available. */
  timeZone?: string;
}

/** "12 Aug 2026" in the given zone. */
export function formatDate(
  value: Date | string | number,
  { timeZone = DEFAULT_TIMEZONE }: DateFormatOptions = {},
): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone,
  }).format(date);
}

/** "12 Aug 2026, 11:45 pm" in the given zone. */
export function formatDateTime(
  value: Date | string | number,
  { timeZone = DEFAULT_TIMEZONE }: DateFormatOptions = {},
): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  }).format(date);
}

/** "11:45 pm" in the given zone. */
export function formatTime(
  value: Date | string | number,
  { timeZone = DEFAULT_TIMEZONE }: DateFormatOptions = {},
): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  }).format(date);
}

/**
 * Countdown prose for a quote's remaining validity: "29 minutes left",
 * "1 minute left", "expired". Screens polling a quote render this, so it takes
 * whole seconds and never shows a jittering seconds counter above a minute.
 */
export function formatTimeLeft(secondsRemaining: number): string {
  const seconds = Math.floor(secondsRemaining);
  if (!Number.isFinite(seconds) || seconds <= 0) return 'expired';
  if (seconds < 60) return `${seconds}s left`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${plural(minutes, 'minute')} left`;
  const hours = Math.floor(minutes / 60);
  return `${plural(hours, 'hour')} left`;
}

/** "in 30 minutes" / "40 minutes ago". Coarse by design: no live-ticking text. */
export function formatRelative(value: Date | string | number, now: Date = new Date()): string {
  const date = toDate(value);
  if (!date) return '';
  const deltaSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const absolute = Math.abs(deltaSeconds);
  const formatter = new Intl.RelativeTimeFormat(DEFAULT_LOCALE, { numeric: 'auto' });
  if (absolute < 60) return formatter.format(deltaSeconds, 'second');
  if (absolute < 3600) return formatter.format(Math.round(deltaSeconds / 60), 'minute');
  if (absolute < 86_400) return formatter.format(Math.round(deltaSeconds / 3600), 'hour');
  return formatter.format(Math.round(deltaSeconds / 86_400), 'day');
}

// --- Misc ------------------------------------------------------------------

/** ".pdf, .PDF" -> "PDF" list for prose like "We accept PDF files". */
export function formatExtensionList(extensions: readonly string[]): string {
  const cleaned = extensions.map((ext) => ext.replace(/^\./, '').toUpperCase());
  const unique = [...new Set(cleaned)];
  if (unique.length <= 1) return unique[0] ?? '';
  return `${unique.slice(0, -1).join(', ')} or ${unique[unique.length - 1] ?? ''}`;
}

/** Title-cases a snake_case or kebab-case server enum for display. */
export function humanise(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/^\w/, (character) => character.toUpperCase());
}
